import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import MenuItem from '@material-ui/core/MenuItem'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Clear from '@material-ui/icons/Clear'
// v4 Select: a v5 Select's menu fights the v4 Dialog's focus trap ("too much recursion")
import Select from '@material-ui/core/Select'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import moment from 'moment-timezone'

import componentStylesDialog from 'assets/theme/components/dialog.js'

const useStylesDialog = makeStyles(componentStylesDialog)

const MATCH_LABELS = {
    stored: 'Saved on record',
    metadata: 'Metadata match',
    customerAmount: 'Customer + amount match',
    pasted: 'Pasted id'
}

const rowKey = (row) => row.companyId + '|' + row.date

const PREVIEW_CHUNK_SIZE = 5

function chunk(items, size) {
    const chunks = []
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size))
    }
    return chunks
}

const formatEur = (value) => (value === undefined || value === null ? '-' : '€' + Number(value).toFixed(2))
const formatAed = (value) => (value === undefined || value === null ? '-' : 'AED ' + Number(value).toFixed(2))

export default function XeroSyncDialog(props) {
    const classes = { ...useStylesDialog() }
    const api = new AdminAPI()

    const [rows, setRows] = useState([])
    const [included, setIncluded] = useState({})
    const [pasted, setPasted] = useState({})
    const [results, setResults] = useState({})
    const [loading, setLoading] = useState(true)
    const [busyRows, setBusyRows] = useState({})
    const [syncing, setSyncing] = useState(false)
    const [alert, setAlert] = useState(null)

    useEffect(() => {
        loadPreview()
    }, [])

    async function loadPreview() {
        setLoading(true)
        try {
            const keys = props.payments.map(p => ({ companyId: p.companyId, date: p.date }))
            let previewed = []
            for (const part of chunk(keys, PREVIEW_CHUNK_SIZE)) {
                const response = await api.xeroSyncPreview(part)
                previewed = previewed.concat(response.rows)
            }
            setRows(previewed)
            const nextIncluded = {}
            // Re-sending an already-synced payment makes the Zap try to add a second Xero payment
            previewed.forEach(r => { nextIncluded[rowKey(r)] = r.valid && !(r.invoice && r.invoice.alreadySynced) })
            setIncluded(nextIncluded)
        } catch(e) {
            onError(e)
        }
        setLoading(false)
    }

    async function previewRow(row, paymentIntentId) {
        const key = rowKey(row)
        setBusyRows(prev => ({ ...prev, [key]: true }))
        try {
            const response = await api.xeroSyncPreview([{ companyId: row.companyId, date: row.date, paymentIntentId }])
            const updated = response.rows[0]
            setRows(prev => prev.map(r => (rowKey(r) === key ? updated : r)))
            setIncluded(prev => ({ ...prev, [key]: updated.valid }))
            setResults(prev => {
                const next = { ...prev }
                delete next[key]
                return next
            })
        } catch(e) {
            onError(e)
        }
        setBusyRows(prev => {
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const selectedId = (row) => (row.selected ? row.selected.paymentIntentId : null)

    // Single-row re-previews can't see the rest of the batch, so duplicates are also checked here
    const duplicateIds = (() => {
        const counts = {}
        rows.forEach(r => {
            const id = selectedId(r)
            if (id) counts[id] = (counts[id] || 0) + 1
        })
        return Object.keys(counts).filter(id => counts[id] > 1)
    })()

    const isRowValid = (row) => row.valid && !duplicateIds.includes(selectedId(row))
    const syncableRows = rows.filter(r => isRowValid(r) && included[rowKey(r)] && !(results[rowKey(r)] && results[rowKey(r)].success))

    function confirmSync() {
        setAlert(
            <ReactBSAlert
                warning
                showCancel
                style={{ display: 'block', marginTop: '-100px' }}
                title='Sync to Xero?'
                onConfirm={() => { setAlert(null); sync() }}
                onCancel={() => setAlert(null)}
                cancelBtnCssClass='btn-secondary'
                confirmBtnBsStyle='primary'
                cancelBtnText='Cancel'
                confirmBtnText={'Sync ' + syncableRows.length + ' payment' + (syncableRows.length === 1 ? '' : 's')}
                btnSize=''
            >
                The selected PaymentIntent will be saved on each payment and sent to the Xero Zap with Stripe's exchange rate. Only sync payments that have no payment recorded in Xero yet, or Xero will reject the duplicate.
            </ReactBSAlert>
        )
    }

    // One row per request keeps each call well inside the API timeout; a PaymentIntent saved
    // on an earlier row fails the server's linked check if it's reused on a later one
    async function sync() {
        setSyncing(true)
        const next = { ...results }
        for (const r of syncableRows) {
            try {
                const response = await api.xeroSync([{
                    companyId: r.companyId,
                    date: r.date,
                    paymentIntentId: selectedId(r)
                }])
                next[rowKey(r)] = response.rows[0]
            } catch(e) {
                next[rowKey(r)] = { success: false, error: e.message }
                onError(e)
            }
            setResults({ ...next })
        }
        if (props.onSynced) {
            props.onSynced()
        }
        setSyncing(false)
    }

    function renderChecks(row) {
        const failures = []
        if (row.error) failures.push(row.error)
        if (row.selected) {
            row.selected.checks.filter(c => !c.ok).forEach(c => failures.push(c.message))
        }
        if (duplicateIds.includes(selectedId(row))) {
            failures.push('The same PaymentIntent is selected for more than one payment in this batch')
        }
        const result = results[rowKey(row)]
        if (result && !result.success) {
            const failedChecks = (result.checks || []).filter(c => !c.ok).map(c => c.message)
            failures.push('Sync failed: ' + (result.error || 'Unknown error'), ...failedChecks)
        }

        if (result && result.success) {
            return <Chip size='small' label='Queued in Zapier' title='Zapier received it; check the Zap history to confirm Xero was updated' style={{ backgroundColor: '#2dce89', color: '#fff' }} />
        }
        if (!failures.length) {
            return <Chip size='small' label='Ready' style={{ backgroundColor: '#11cdef', color: '#fff' }} />
        }
        return (
            <Box>
                {failures.map((f, i) => (
                    <Typography key={i} variant='body2' style={{ color: '#f5365c', fontSize: '0.75rem' }}>{f}</Typography>
                ))}
            </Box>
        )
    }

    function renderRow(row) {
        const key = rowKey(row)
        const selected = row.selected
        const busy = !!busyRows[key]
        const valid = isRowValid(row)
        const done = !!(results[key] && results[key].success)

        return (
            <TableRow key={key}>
                <TableCell padding='checkbox'>
                    <Checkbox
                        checked={valid && !done && !!included[key]}
                        disabled={!valid || done || busy || syncing}
                        onChange={(e) => setIncluded(prev => ({ ...prev, [key]: e.target.checked }))}
                    />
                </TableCell>
                <TableCell style={{ minWidth: '220px' }}>{renderChecks(row)}</TableCell>
                <TableCell>
                    {row.invoice ? row.invoice.companyName : row.companyId}
                    {row.invoice && row.invoice.alreadySynced && (
                        <Chip size='small' label='Already synced' title='Re-sending will make the Zap try to add another payment in Xero' style={{ marginLeft: '0.5rem', fontSize: '0.65rem', height: '20px' }} />
                    )}
                </TableCell>
                <TableCell>{moment(row.date).format('DD/MM/YYYY')}</TableCell>
                <TableCell>{row.invoice ? formatEur(row.invoice.amountDue) : '-'}</TableCell>
                <TableCell style={{ wordBreak: 'break-all' }}>{row.invoice ? (row.invoice.reference || '-') : '-'}</TableCell>
                <TableCell style={{ minWidth: '260px' }}>
                    {row.candidates.length > 0 && (
                        <Select
                            variant='outlined'
                            margin='dense'
                            fullWidth
                            value={selectedId(row) && row.candidates.some(c => c.paymentIntentId === selectedId(row)) ? selectedId(row) : ''}
                            disabled={busy || syncing || done}
                            onChange={(e) => previewRow(row, e.target.value)}
                        >
                            {row.candidates.map(c => (
                                <MenuItem key={c.paymentIntentId} value={c.paymentIntentId}>
                                    {c.paymentIntentId} · {formatEur(c.amountEur)} · {moment(c.chargeDate).format('DD/MM/YY')}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    <Box display='flex' marginTop='0.5rem'>
                        <OutlinedInput
                            margin='dense'
                            fullWidth
                            placeholder='Paste pi_…'
                            value={pasted[key] || ''}
                            disabled={busy || syncing}
                            onChange={(e) => {
                                const value = e.target.value
                                setPasted(prev => ({ ...prev, [key]: value }))
                            }}
                        />
                        <Button
                            size='small'
                            color='primary'
                            disabled={busy || syncing || !(pasted[key] || '').trim()}
                            onClick={() => previewRow(row, pasted[key].trim())}
                        >
                            {busy ? '…' : 'Preview'}
                        </Button>
                    </Box>
                </TableCell>
                <TableCell>{selected ? moment(selected.chargeDate).format('DD/MM/YY HH:mm') : '-'}</TableCell>
                <TableCell>{selected ? selected.paymentDate : '-'}</TableCell>
                <TableCell>{selected ? formatEur(selected.amountEur) : '-'}</TableCell>
                <TableCell>{selected && selected.cardLast4 ? '•••• ' + selected.cardLast4 : '-'}</TableCell>
                <TableCell>{selected && selected.stripeRate !== undefined ? selected.stripeRate : '-'}</TableCell>
                <TableCell>
                    {selected && selected.xeroCurrencyRate !== undefined
                        ? selected.xeroCurrencyRate
                        : (selected ? <span title='Xero will use its own rate'>None</span> : '-')}
                </TableCell>
                <TableCell>{selected ? formatAed(selected.amountAed) : '-'}</TableCell>
                <TableCell>{selected ? (MATCH_LABELS[selected.matchType] || selected.matchType) : '-'}</TableCell>
            </TableRow>
        )
    }

    const resultList = Object.values(results)
    const queuedCount = resultList.filter(r => r.success).length
    const failedCount = resultList.length - queuedCount

    return (
        <Dialog
            open={props.isOpen}
            onClose={() => props.close()}
            maxWidth={false}
            PaperProps={{ style: { width: '95vw', maxWidth: '95vw' } }}
        >
            {alert}
            <div className={classes.dialogHeader}>
                <Typography variant='h5' component='h5' className={classes.dialogTitle}>
                    Sync to Xero
                </Typography>
                <IconButton onClick={() => props.close()}>
                    <Clear />
                </IconButton>
            </div>
            <DialogContent>
                {loading ? (
                    <Loader />
                ) : (
                    <Box style={{ overflowX: 'auto' }}>
                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding='checkbox' />
                                    <TableCell>Status</TableCell>
                                    <TableCell>Company</TableCell>
                                    <TableCell>Invoice date</TableCell>
                                    <TableCell>Amount due</TableCell>
                                    <TableCell>Xero reference</TableCell>
                                    <TableCell>PaymentIntent</TableCell>
                                    <TableCell>Charged</TableCell>
                                    <TableCell>Payment date</TableCell>
                                    <TableCell>EUR</TableCell>
                                    <TableCell>Card</TableCell>
                                    <TableCell>Stripe rate</TableCell>
                                    <TableCell>Xero rate</TableCell>
                                    <TableCell>AED</TableCell>
                                    <TableCell>Match</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(renderRow)}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Box width='100%' display='flex' justifyContent='space-between'>
                    <Button onClick={() => props.close()} color='primary'>
                        Close
                    </Button>
                    {resultList.length > 0 && (
                        <Typography variant='body2' style={{ alignSelf: 'center', color: failedCount ? '#f5365c' : '#2dce89' }}>
                            {queuedCount} queued in Zapier{failedCount ? ', ' + failedCount + ' failed' : ''}. Check the Zap history to confirm Xero was updated.
                        </Typography>
                    )}
                    <Button
                        color='primary'
                        variant='contained'
                        disabled={loading || syncing || syncableRows.length === 0}
                        onClick={() => confirmSync()}
                    >
                        {syncing ? 'Syncing…' : 'Sync ' + syncableRows.length + ' to Xero'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
